import React from 'react';
import { get as _get } from 'lodash';
import { ProfileData, SocialPost, CommentType } from '../../types';
import SocialPostCard from '../social/SocialPostCard';
import Icon from '../common/Icon';
import Loader from '../common/Loader';
import ReadMoreText from '../common/ReadMoreText';

interface SocialTabProps {
  profile: ProfileData;
  selectedPostIdx: number | null;
  onSelectPost: (idx: number) => void;
  onCopy: (text: string) => void;
  onShare: (post: SocialPost) => void;
  onGenerateComment: (idx: number, type: CommentType) => Promise<void>;
  socialComment: string;
  setSocialComment: (comment: string) => void;
  isGenerating: boolean;
}

const SocialTab: React.FC<SocialTabProps> = ({
  profile,
  selectedPostIdx,
  onSelectPost,
  onCopy,
  onShare,
  onGenerateComment,
  socialComment,
  setSocialComment,
  isGenerating,
}) => {
  const posts = _get(profile, 'socialActivity.posts') || [];
  const profileName = _get(profile, 'name') || '';
  
  const selectedPost = selectedPostIdx !== null ? posts[selectedPostIdx] : null;
  const selectedContent = selectedPost ? (_get(selectedPost, 'content') || '') : '';

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-6 max-h-[800px] overflow-y-auto no-scrollbar pr-2 pb-10">
          <h3 className="text-xl font-extrabold flex items-center gap-2 text-brand-black sticky top-0 bg-[#fcfcfc] z-10 py-4">
            <Icon name="rss_feed" className="text-brand-pink" />
            Recent Social Activity
          </h3>
          
          {!posts || posts.length === 0 ? (
            <p className="text-brand-grey08 text-sm italic">No social activity available.</p>
          ) : (
            <div className="space-y-6 p-5">
              {(posts || []).map((post: SocialPost, idx: number) => (
                <SocialPostCard
                  key={_get(post, 'id', idx)}
                  post={post}
                  profileName={profileName}
                  isSelected={selectedPostIdx === idx}
                  onSelect={() => onSelectPost(idx)}
                  onCopy={onCopy}
                  onShare={onShare}
                  onGenerateComment={(type) => onGenerateComment(idx, type)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="bg-brand-black text-white rounded-brand p-10 h-full relative overflow-hidden flex flex-col shadow-2xl transition-all duration-500 sticky top-28 min-h-[600px]">
            <h3 className="text-2xl font-extrabold mb-6 flex items-center gap-2 text-brand-aqua">
              <Icon name="forum" />
              Engagement Workbench
            </h3>
            
            <div className="p-8 bg-brand-grey09 border-2 border-brand-grey08 rounded-brand italic text-xl leading-relaxed text-brand-grey02 mb-10 border-l-8 border-l-brand-pink animate-in slide-in-from-left duration-700">
              {selectedContent
                ? <ReadMoreText text={selectedContent} charLimit={350} />
                : 'Click a post and select an action to start drafting a behavioral response...'}
            </div>

            <div className="flex-1 relative flex flex-col">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 text-brand-grey02">
                AI Generated Strategy
              </label>
              <textarea
                value={socialComment}
                onChange={(e) => setSocialComment(e.target.value)}
                className="flex-1 bg-brand-grey09 border border-brand-grey08 rounded-field p-8 text-sm outline-none focus:border-brand-aqua resize-none font-medium leading-relaxed font-sans placeholder-brand-grey08"
                placeholder={
                  isGenerating
                    ? 'Processing psychological triggers...'
                    : 'Your draft will be synthesized here...'
                }
              />
              {isGenerating && (
                <div className="absolute inset-0 bg-brand-grey09/70 rounded-field flex flex-col items-center justify-center gap-5">
                  <Loader size="lg" color="pink" text="Building Context" />
                </div>
              )}
            </div>

            {socialComment && (
              <button
                onClick={() => onCopy(socialComment)}
                className="mt-8 bg-brand-pink text-white font-black py-5 rounded-pill text-xs flex items-center justify-center gap-2 hover:bg-white hover:text-brand-black transition-all shadow-xl active:scale-95 group"
              >
                <Icon
                  name="content_copy"
                  size="sm"
                  className="group-hover:scale-125 transition-transform"
                />
                Copy to Clipboard
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialTab;
